import { useState } from 'react'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AircraftTypesTable } from '@/components/aircraft-types/AircraftTypesTable'
import { AircraftTypeSidePanel } from '@/components/aircraft-types/AircraftTypeSidePanel'
import { DeleteAircraftTypeDialog } from '@/components/aircraft-types/DeleteAircraftTypeDialog'
import { useAircraftTypes } from '@/hooks/useAircraftTypes'

/**
 * Aircraft Types management page (Admin only)
 * Displays list of aircraft types with CRUD operations
 */
export function AircraftTypesPage() {
  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [editAircraftTypeId, setEditAircraftTypeId] = useState<string | undefined>()
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string
    name: string
  } | null>(null)

  // Fetch aircraft types data
  const { data: aircraftTypes, isLoading } = useAircraftTypes()

  const handleEdit = (id: string) => {
    setEditAircraftTypeId(id)
  }

  const handleDelete = (id: string, name: string) => {
    setDeleteTarget({ id, name })
  }

  const handleCloseEditDialog = (open: boolean) => {
    if (!open) {
      setEditAircraftTypeId(undefined)
    }
  }

  const handleCloseDeleteDialog = (open: boolean) => {
    if (!open) {
      setDeleteTarget(null)
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Aircraft Types</h1>
          <p className="text-muted-foreground">
            Manage global aircraft reference data and 3D model parameters
          </p>
        </div>
        <Button onClick={() => setCreateDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Aircraft Type
        </Button>
      </div>

      {/* Aircraft Types Table */}
      <AircraftTypesTable
        data={aircraftTypes || []}
        isLoading={isLoading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      {/* Create Aircraft Type Side Panel */}
      <AircraftTypeSidePanel
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {/* Edit Aircraft Type Side Panel */}
      <AircraftTypeSidePanel
        open={!!editAircraftTypeId}
        onOpenChange={handleCloseEditDialog}
        aircraftTypeId={editAircraftTypeId}
      />

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <DeleteAircraftTypeDialog
          open={!!deleteTarget}
          onOpenChange={handleCloseDeleteDialog}
          aircraftTypeId={deleteTarget.id}
          aircraftTypeName={deleteTarget.name}
        />
      )}
    </div>
  )
}
